<template>
  <!--
    Filter row: condition toggle (2/12) | criteria + countries (8/12)
    | remove button (2/12). Inner block has its own nested grid so the
    criteria select shrinks from full-width to 4/12 once the user
    picks a criterion (revealing the countries multi-select on the
    right at 8/12 on lg+).
  -->
  <div class="tw-grid tw-grid-cols-12 tw-items-baseline">
    <div class="tw-col-span-2 tw-text-end">
      <UiButton
        v-if="showCondition"
        type="button"
        variant="outline-primary"
        @click="toggleCondition"
      >
        {{ filter.condition }}
      </UiButton>
    </div>

    <div class="tw-col-span-8">
      <div class="tw-grid tw-grid-cols-12 p-2">
        <div :class="filter.criteria ? 'lg:tw-col-span-4 tw-col-span-12' : 'tw-col-span-12'">
          <UiSelect
            ref="criteria"
            :model-value="filter.criteria"
            :options="countryCriteriaOptions"
            :tabindex="(tabIndex * 2) + 1"
            @update:model-value="val => onCriteriaChange(val)"
          >
            <template #first>
              <option
                :value="null"
                disabled
              >
                No filter
              </option>
            </template>
          </UiSelect>
        </div>

        <div
          class="lg:tw-col-span-8 tw-col-span-12"
          v-if="!!filter.criteria"
        >
          <UiSearchSelect
            multiple
            :options="countries"
            :model-value="selectedCountryIds"
            @update:model-value="onCountriesChange"
            placeholder="Countries"
            value-field="id"
            text-field="name"
          />
        </div>
      </div>
    </div>

    <div class="tw-col-span-2 tw-text-start">
      <UiButton
        variant="outline-secondary"
        @click="onRemove"
        tabindex="-1"
      >
        <i class="fa fa-minus" />
      </UiButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, useTemplateRef } from 'vue'
import type { PropType } from 'vue'
import { storeToRefs } from 'pinia'

import { useHeaderStore } from '@/stores/header'

import { countryCriteriaOptions, AND, OR } from '.'

const props = defineProps({
  showCondition: {
    type: Boolean,
    default: true,
    required: false
  },
  filter: {
    type: Object as PropType<Record<string, any>>,
    default: () => {},
    required: false
  },
  onRemove: {
    type: Function,
    required: true
  },
  afterUpdate: {
    type: Function,
    default: () => {},
    required: false
  },
  tabIndex: {
    type: Number,
    default: 0,
    required: false
  }
})

const emit = defineEmits(['update:filter'])

const headerStore = useHeaderStore()
const { countryDataGetter: countries } = storeToRefs(headerStore)

const criteria = useTemplateRef('criteria')

function updateFilter (field: string, value: any) {
  emit('update:filter', { ...props.filter, [field]: value })
  props.afterUpdate(field, value)
}

function toggleCondition () {
  const newCondition = props.filter.condition === AND ? OR : AND
  updateFilter('condition', newCondition)
}

function onCriteriaChange (val: any) {
  updateFilter('criteria', val)
}

// UiSearchSelect speaks scalar ids; the saved filter holds full country
// objects so downstream filter consumers (CountryFilter index, store
// payload) keep working unchanged. Wrap/unwrap at the v-model boundary.
const selectedCountryIds = computed<number[]>(() =>
  (props.filter.countries ?? []).map((c: { id: number }) => c.id)
)

function onCountriesChange (ids: number[]) {
  const next = ids
    .map(id => countries.value.find((c: { id: number }) => c.id === id))
    .filter(Boolean)
  updateFilter('countries', next)
}

// created hook equivalent — focus the criteria select if possible
nextTick(() => {
  if (criteria.value) {
    const el = (criteria.value as any).$el || criteria.value
    if (typeof el.focus === 'function') el.focus()
  }
})
</script>

<style scoped>
  /* The Semantic UI dropdown overrides previously here have been
     removed — vue-search-select is gone, and UiSearchSelect inherits
     its chrome from the design-system primitive directly. */
</style>
